// The benchmark's two HTTP calls, in the `lobby-http.ts` / `replay-http.ts` mould: a `fetch`
// client and nothing more. Typed refusals as VALUES, never thrown exceptions — the house rule
// every other client surface follows.
//
// Both calls are root-relative. The SPA is served same-origin behind Caddy, and there is no
// `/api` prefix anywhere (see `client/vite.config.ts`'s header for the escalation finding that
// settled that). The device already fetched the whole client from this server, so neither call
// involves a membership, a join code or a credential.

import { BENCHMARK_REPORT_PATH, type BenchmarkReport } from './report';

// The preset GET lives in `benchmark-sim-phase.ts` beside its only caller, not here. It was
// briefly defined in both files, which meant two literals for one path — exactly the drift this
// module exists to prevent. One source of truth: `BENCHMARK_PRESET_PATH` is exported there.

/** The submission's answer. */
export type SubmitOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'failed'; readonly detail: string };

/** The `fetch` seam, so tests need no network and no global patching. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

function fetcher(override?: FetchLike): FetchLike {
  return override ?? ((input, init) => globalThis.fetch(input, init));
}

/**
 * Submits the one report.
 *
 * A failed submission is a reportable outcome and **never** discards the results: the device is
 * often borrowed and unreachable afterwards, so what is on screen has to survive a failed
 * upload. The measurement is the expensive part; the POST is not.
 */
export async function submitBenchmarkReport(
  report: BenchmarkReport,
  fetchImpl?: FetchLike,
  path: string = BENCHMARK_REPORT_PATH,
): Promise<SubmitOutcome> {
  let response: Response;
  try {
    response = await fetcher(fetchImpl)(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
    });
  } catch (error) {
    return { kind: 'failed', detail: `the report could not be sent: ${String(error)}` };
  }
  if (!response.ok) {
    return { kind: 'failed', detail: `the server answered ${response.status}` };
  }
  return { kind: 'ok' };
}
